import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Archive, BookLock, Download, FileSignature, Plus, RotateCw, Trash2 } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "../../api/client";
import * as registroApi from "../../api/registroAffari";
import { EsitoAffare, RigaRegistroAffari } from "../../api/registroAffari";
import * as contrattiApi from "../../api/contratti";
import { Contratto } from "../../api/contratti";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge, BadgeColor } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { downloadProtectedFile } from "../../api/files";
import { usePannelloRecord, useOrdinamentoUrl, useRicercaUrl, useScriviParametri } from "../../hooks/useStatoUrl";

// ============================================================================
// REGISTRO GIORNALE DEGLI AFFARI
//
// Non è una pagina come le altre, ed è il motivo di quasi ogni scelta che
// segue: quello che si vede qui è un REGISTRO DI LEGGE (art. 120 TULPS,
// contenuto dall'art. 219 del R.D. 635/1940), non un elenco di record.
//
// Conseguenze visibili:
//
// - le righe si leggono UN ANNO ALLA VOLTA, perché il progressivo riparte da 1
//   a ogni anno solare e il registro di carta è un fascicolo per anno;
// - l'ordine è quello del registro — numero d'ordine crescente, non «il più
//   recente in cima»: su un giornale si legge dall'inizio;
// - una data che manca si scrive «non risulta» e non con un trattino qualunque,
//   perché su otto righe storiche su dodici è un'assenza vera e dichiarata;
// - la cancellazione è distante e solo per admin.
//
// Le righe dei contratti firmati QUI NON SI CREANO: nascono da sole alla firma.
// Il pulsante serve all'affare che da un contratto Cash Management non viene, e
// allo storico caricato a posteriori.
// ============================================================================

// ============================================================================
// IL PERIODO — scelto allo stesso modo per l'estratto e per l'archivio
// ============================================================================

/** Lo stato del selettore di periodo, tenuto da chi lo usa. */
interface SceltaPeriodo {
  modo: "anno" | "intervallo";
  anno: number;
  da: string;
  a: string;
}

function sceltaIniziale(anno: number): SceltaPeriodo {
  return { modo: "anno", anno, da: "", a: "" };
}

/** Il periodo come lo vuole l'anteprima: l'anno, oppure l'intervallo completo. */
function periodoDaScelta(s: SceltaPeriodo): registroApi.PeriodoEstratto | null {
  return s.modo === "anno" ? { anno: s.anno } : s.da && s.a ? { da: s.da, a: s.a } : null;
}

/** Gli estremi come date: l'archivio salva sempre `da` e `a`, anche per un anno intero. */
function estremiDaScelta(s: SceltaPeriodo): { da: string; a: string } | null {
  if (s.modo === "anno") return { da: `${s.anno}-01-01`, a: `${s.anno}-12-31` };
  return s.da && s.a ? { da: s.da, a: s.a } : null;
}

const CLASSE_SELECT =
  "h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink shadow-card focus:border-primary/40 focus:outline-none";

/**
 * Il selettore di periodo, uno solo per l'estratto e per l'archivio.
 *
 * Uno solo di proposito: si archivia il periodo che si è estratto, e se i due
 * selettori leggessero un anno in due modi diversi l'archivio direbbe un
 * periodo diverso da quello del documento firmato.
 */
function SelettorePeriodo({
  valore,
  onChange,
  anni,
  annoCorrente,
  etichetta,
}: {
  valore: SceltaPeriodo;
  onChange: (s: SceltaPeriodo) => void;
  anni: number[];
  annoCorrente: number;
  etichetta: string;
}) {
  // Gli anni del registro, quello corrente e quello scelto: un fascicolo può
  // essere ancora vuoto e va comunque potuto scegliere.
  const selezionabili = [...new Set([...anni, annoCorrente, valore.anno])].sort((x, y) => y - x);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Button
          variant={valore.modo === "anno" ? "primary" : "secondary"}
          size="sm"
          onClick={() => onChange({ ...valore, modo: "anno" })}
        >
          Anno solare
        </Button>
        <Button
          variant={valore.modo === "intervallo" ? "primary" : "secondary"}
          size="sm"
          onClick={() => onChange({ ...valore, modo: "intervallo" })}
        >
          Intervallo di date
        </Button>
      </div>
      {valore.modo === "anno" ? (
        <select
          value={valore.anno}
          onChange={(e) => onChange({ ...valore, anno: Number(e.target.value) })}
          className={CLASSE_SELECT}
          aria-label={etichetta}
        >
          {selezionabili.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Input label="Dal" type="date" value={valore.da} onChange={(e) => onChange({ ...valore, da: e.target.value })} />
          <Input label="Al" type="date" value={valore.a} onChange={(e) => onChange({ ...valore, a: e.target.value })} />
        </div>
      )}
    </div>
  );
}

/**
 * Quante registrazioni comprende il periodo, rifatto a ogni cambio: la cifra a
 * schermo deve sempre corrispondere al periodo scelto.
 */
function useAnteprimaPeriodo(attivo: boolean, periodo: registroApi.PeriodoEstratto | null) {
  const { showToast } = useToast();
  const [anteprima, setAnteprima] = useState<registroApi.AnteprimaEstratto | null>(null);
  const [isCounting, setIsCounting] = useState(false);
  // La chiave è il periodo scritto come testo: l'oggetto cambia identità a
  // ogni render e farebbe ripartire la richiesta all'infinito.
  const chiave = attivo && periodo ? JSON.stringify(periodo) : null;

  useEffect(() => {
    if (!chiave) {
      setAnteprima(null);
      setIsCounting(false);
      return;
    }
    let annullato = false;
    setIsCounting(true);
    registroApi
      .anteprimaEstratto(JSON.parse(chiave) as registroApi.PeriodoEstratto)
      .then((r) => {
        if (!annullato) setAnteprima(r);
      })
      .catch((err) => {
        if (!annullato) {
          setAnteprima(null);
          showToast(getErrorMessage(err, "Impossibile contare le registrazioni del periodo."), "error");
        }
      })
      .finally(() => {
        if (!annullato) setIsCounting(false);
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiave]);

  return { anteprima, isCounting };
}

/** Il riquadro col conteggio, uguale nell'estratto e nell'archivio. */
function RiepilogoPeriodo({
  anteprima,
  isCounting,
  vuoto,
}: {
  anteprima: registroApi.AnteprimaEstratto | null;
  isCounting: boolean;
  vuoto: string;
}) {
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      {isCounting ? (
        <p className="text-sm text-ink/50">Conteggio in corso…</p>
      ) : !anteprima ? (
        <p className="text-sm text-ink/50">Indica il periodo per vedere quante registrazioni comprende.</p>
      ) : anteprima.totale === 0 ? (
        <p className="text-sm text-ink/60">{vuoto}</p>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-ink">
            {anteprima.totale} {anteprima.totale === 1 ? "registrazione" : "registrazioni"}
          </p>
          <ul className="flex flex-col gap-1 text-sm text-ink/70">
            {anteprima.per_anno.map((r) => (
              <li key={r.anno}>
                <span className="font-medium text-ink">{r.anno}</span> — {r.quante}{" "}
                {r.quante === 1 ? "registrazione" : "registrazioni"}, dal numero d'ordine{" "}
                <span className="tabular-nums">{r.dal}</span> al <span className="tabular-nums">{r.al}</span>
              </li>
            ))}
          </ul>
          {anteprima.senza_data > 0 && (
            // Detto qui e non solo sul PDF: è la differenza fra questo
            // periodo e uno che filtrasse solo sulle date, e chi firma deve
            // saperlo prima di aprire il file.
            <p className="text-xs text-ink/50">
              {anteprima.senza_data === 1
                ? "Una registrazione è compresa"
                : `${anteprima.senza_data} registrazioni sono comprese`}{" "}
              pur non avendo una data: il registro non la riporta, e ci rientrano per l'anno del numero
              d'ordine.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * L'ESTRATTO DEL PERIODO — il documento che si firma.
 *
 * Il conteggio si vede PRIMA di scaricare, e non è una gentilezza: qui si
 * sceglie cosa finirà dentro un PDF che verrà firmato digitalmente e marcato
 * temporalmente. Se il numero non torna, ce ne si accorge adesso e non dopo la
 * firma.
 */
function EstrattoModal({
  open,
  onClose,
  anni,
  annoCorrente,
}: {
  open: boolean;
  onClose: () => void;
  anni: number[];
  annoCorrente: number;
}) {
  const { showToast } = useToast();
  const [scelta, setScelta] = useState<SceltaPeriodo>(() => sceltaIniziale(annoCorrente));
  const periodo = periodoDaScelta(scelta);
  const { anteprima, isCounting } = useAnteprimaPeriodo(open, periodo);
  const [isDownloading, setIsDownloading] = useState(false);

  async function scarica() {
    if (!periodo || !anteprima) return;
    setIsDownloading(true);
    try {
      await registroApi.scaricaEstratto(periodo, `RGA_EVOsrl_${anteprima.periodo.da}_${anteprima.periodo.a}.pdf`);
      showToast("Estratto scaricato.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile generare l'estratto."), "error");
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Estratto per la firma"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Chiudi
          </Button>
          <Button onClick={scarica} loading={isDownloading} disabled={!anteprima || anteprima.totale === 0}>
            Scarica PDF
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink/60">
          Il PDF va firmato digitalmente e marcato temporalmente fuori dall'app, con il processo Aruba già in
          uso. Controlla il conteggio prima di scaricarlo: è quello che finirà nel documento firmato.
        </p>
        <SelettorePeriodo
          valore={scelta}
          onChange={setScelta}
          anni={anni}
          annoCorrente={annoCorrente}
          etichetta="Anno dell'estratto"
        />
        <RiepilogoPeriodo
          anteprima={anteprima}
          isCounting={isCounting}
          vuoto="Nessuna registrazione in questo periodo: non c'è niente da estrarre."
        />
      </div>
    </Modal>
  );
}

// ============================================================================
// GLI ARCHIVI DEI PERIODI VIDIMATI (Fase 6)
// ============================================================================

/**
 * Una casella per un file. `accept` sull'ESTENSIONE e non sul tipo: il sistema
 * operativo non conosce i `.tsd` e i `.p7m`, e con un filtro sul tipo il file
 * giusto non comparirebbe nemmeno nella finestra di scelta.
 */
function CampoFile({
  etichetta,
  estensione,
  obbligatorio,
  file,
  onChange,
  nota,
}: {
  etichetta: string;
  estensione: string;
  obbligatorio?: boolean;
  file: File | null;
  onChange: (f: File | null) => void;
  nota: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {etichetta} <span className="font-normal text-ink/40">({estensione})</span>
        {obbligatorio && <span className="text-accent"> *</span>}
      </label>
      <input
        id={id}
        type="file"
        accept={estensione}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        className="text-sm text-ink file:mr-3 file:rounded-lg file:border file:border-line file:bg-surface file:px-3 file:py-1.5 file:text-sm file:text-ink hover:file:bg-white"
      />
      <p className="text-xs text-ink/50">
        {file ? `${file.name} — ${Math.max(1, Math.round(file.size / 1024))} KB` : nota}
      </p>
    </div>
  );
}

/**
 * ARCHIVIARE UN PERIODO FIRMATO E MARCATO — il secondo tempo dell'estratto.
 *
 * Si scarica l'estratto, lo si porta da Aruba, e quello che torna si carica
 * qui. Il conteggio è lo stesso dell'estratto, ed è l'ultimo controllo che
 * nessun altro fa: se il periodo dice 14 registrazioni, il PDF che si sta
 * caricando deve averne 14.
 */
function ArchiviaModal({
  open,
  onClose,
  anni,
  annoCorrente,
  archivi,
  onArchiviato,
}: {
  open: boolean;
  onClose: () => void;
  anni: number[];
  annoCorrente: number;
  archivi: registroApi.ArchivioRegistro[];
  onArchiviato: () => void;
}) {
  const { showToast } = useToast();
  // L'anno appena chiuso: di norma è quello che si è appena firmato.
  const [scelta, setScelta] = useState<SceltaPeriodo>(() => sceltaIniziale(annoCorrente - 1));
  const [dataMarcatura, setDataMarcatura] = useState("");
  const [pdf, setPdf] = useState<File | null>(null);
  const [tsd, setTsd] = useState<File | null>(null);
  const [p7m, setP7m] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Le caselle file non si possono svuotare da codice: si ricreano, cambiando
  // la chiave a ogni apertura.
  const [giro, setGiro] = useState(0);

  useEffect(() => {
    if (!open) return;
    setScelta(sceltaIniziale(annoCorrente - 1));
    setDataMarcatura("");
    setPdf(null);
    setTsd(null);
    setP7m(null);
    setGiro((g) => g + 1);
  }, [open, annoCorrente]);

  const { anteprima, isCounting } = useAnteprimaPeriodo(open, periodoDaScelta(scelta));
  const estremi = estremiDaScelta(scelta);
  const alContrario = Boolean(estremi && estremi.da > estremi.a);

  // Il periodo identico lo rifiuterebbe il server: dirlo prima evita di
  // caricare tre file per sentirselo dire. Una sovrapposizione parziale
  // invece può essere legittima: si avvisa, non si blocca.
  const identico = estremi
    ? archivi.find((x) => x.periodo_da.slice(0, 10) === estremi.da && x.periodo_a.slice(0, 10) === estremi.a)
    : undefined;
  const sovrapposti =
    estremi && !identico
      ? archivi.filter((x) => x.periodo_da.slice(0, 10) <= estremi.a && x.periodo_a.slice(0, 10) >= estremi.da)
      : [];
  const marcaPrimaDellaFine = Boolean(estremi && dataMarcatura && dataMarcatura < estremi.a);
  const pronto = Boolean(estremi && pdf && dataMarcatura && !identico && !alContrario);

  async function archivia() {
    if (!estremi || !pdf || !pronto) return;
    setIsSaving(true);
    try {
      const archivio = await registroApi.creaArchivio({
        periodo_da: estremi.da,
        periodo_a: estremi.a,
        data_marcatura: dataMarcatura,
        pdf,
        tsd,
        p7m,
      });
      if (archivio.copia_esterna_at) {
        showToast("Periodo archiviato e copiato su SharePoint.", "success");
      } else {
        // Non è un errore: l'archivio c'è. Manca la seconda copia, e si dice
        // perché — la si ritenta dall'elenco.
        showToast(
          `Periodo archiviato, ma la copia su SharePoint non è riuscita: ${
            archivio.copia_esterna_errore ?? "motivo sconosciuto"
          } Si può ritentare con «Ricopia».`,
          "warning"
        );
      }
      onArchiviato();
      onClose();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile archiviare il periodo."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Archivia periodo firmato"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={archivia} loading={isSaving} disabled={!pronto}>
            Archivia
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink/60">
          Carica quello che è tornato da Aruba per il periodo che hai estratto. L'app conserva i file e ne fa
          una copia su SharePoint; <strong>non verifica né la firma né la marca temporale</strong> — quel
          controllo resta quello che fai con Aruba.
        </p>
        <SelettorePeriodo
          valore={scelta}
          onChange={setScelta}
          anni={anni}
          annoCorrente={annoCorrente}
          etichetta="Anno del periodo firmato"
        />
        <RiepilogoPeriodo
          anteprima={anteprima}
          isCounting={isCounting}
          vuoto="Nessuna registrazione in questo periodo: controlla di aver scelto il periodo giusto."
        />
        {identico && (
          <p className="text-sm font-medium text-danger">
            Questo periodo è già archiviato. Per sostituirlo va prima tolto l'archivio esistente — solo un
            admin, e con un motivo.
          </p>
        )}
        {alContrario && <p className="text-sm font-medium text-danger">La data iniziale viene dopo quella finale.</p>}
        {sovrapposti.length > 0 && (
          <p className="text-xs text-ink/60">
            Il periodo si sovrappone a{" "}
            {sovrapposti
              .map((x) => `dal ${dataIT(x.periodo_da)} al ${dataIT(x.periodo_a)}`)
              .join(", ")}
            , già archiviato. Non è un errore, ma verifica che sia voluto.
          </p>
        )}
        <Input
          label="Data della marca temporale"
          type="date"
          required
          value={dataMarcatura}
          onChange={(e) => setDataMarcatura(e.target.value)}
          hint="Quella della ricevuta Aruba."
        />
        {marcaPrimaDellaFine && (
          <p className="text-xs text-ink/70">
            <strong>Attenzione:</strong> la marca è precedente alla fine del periodo. Un estratto firmato
            prima che il periodo si chiudesse potrebbe non comprendere tutte le registrazioni.
          </p>
        )}
        <div key={giro} className="flex flex-col gap-4">
          <CampoFile
            etichetta="PDF firmato"
            estensione=".pdf"
            obbligatorio
            file={pdf}
            onChange={setPdf}
            nota="L'unico dei tre che si apre senza strumenti."
          />
          <CampoFile
            etichetta="Marca temporale"
            estensione=".tsd"
            file={tsd}
            onChange={setTsd}
            nota="Facoltativo."
          />
          <CampoFile
            etichetta="Firma"
            estensione=".p7m"
            file={p7m}
            onChange={setP7m}
            nota="Facoltativo: a volte Aruba restituisce tutta la catena in un solo .p7m."
          />
        </div>
      </div>
    </Modal>
  );
}

const COLORE_VIDIMAZIONE: Record<registroApi.StatoVidimazione, BadgeColor> = {
  vidimato: "success",
  parziale: "warning",
  scoperto: "danger",
  // Neutro e non rosso: un anno non ancora chiuso non si può vidimare, e
  // colorarlo come un'omissione sarebbe un allarme senza rimedio.
  in_corso: "neutral",
};

const ETICHETTA_FILE: Record<registroApi.FileArchivio, string> = { pdf: "PDF", tsd: "TSD", p7m: "P7M" };

/**
 * PERIODI VIDIMATI — gli archivi, e gli anni del registro che non ne hanno.
 *
 * La seconda metà è quella che conta: un elenco dei soli archivi presenti,
 * con un archivio solo, sembrerebbe completo. La vista dice anche cosa manca,
 * finché non viene firmato e marcato (decisione di Fabio del 10/09/2026).
 */
function SezioneVidimazioni({
  dati,
  puoArchiviare,
  puoTogliere,
  inCopia,
  onRicopia,
  onTogli,
}: {
  dati: { archivi: registroApi.ArchivioRegistro[]; anni: registroApi.CoperturaAnno[]; sharepoint_configurato: boolean } | null;
  puoArchiviare: boolean;
  puoTogliere: boolean;
  inCopia: string | null;
  onRicopia: (a: registroApi.ArchivioRegistro) => void;
  onTogli: (a: registroApi.ArchivioRegistro) => void;
}) {
  const { showToast } = useToast();

  async function scarica(a: registroApi.ArchivioRegistro, tipo: registroApi.FileArchivio, url: string) {
    try {
      await downloadProtectedFile(url, registroApi.nomeFileArchivio(a, tipo));
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile scaricare il file."), "error");
    }
  }

  const colonne: TableColumn<registroApi.ArchivioRegistro>[] = [
    {
      key: "periodo",
      header: "Periodo",
      sempreVisibile: true,
      render: (a) => (
        <span className="tabular-nums text-ink">
          dal {dataIT(a.periodo_da)} al {dataIT(a.periodo_a)}
        </span>
      ),
    },
    { key: "registrazioni", header: "Registrazioni", render: (a) => <span className="tabular-nums">{a.registrazioni}</span> },
    { key: "marcatura", header: "Marca temporale", render: (a) => <span className="tabular-nums">{dataIT(a.data_marcatura)}</span> },
    {
      key: "file",
      header: "File",
      render: (a) => (
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              ["pdf", a.file_pdf_url],
              ["tsd", a.file_tsd_url],
              ["p7m", a.file_p7m_url],
            ] as [registroApi.FileArchivio, string | null][]
          )
            .filter((x): x is [registroApi.FileArchivio, string] => x[1] !== null)
            .map(([tipo, url]) => (
              <button
                key={tipo}
                type="button"
                onClick={() => scarica(a, tipo, url)}
                className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface px-2 py-1 text-xs font-medium text-ink/80 transition-[background-color,box-shadow] duration-150 ease-out hover:bg-white hover:shadow-card"
                aria-label={`Scarica il file ${ETICHETTA_FILE[tipo]} del periodo dal ${dataIT(a.periodo_da)} al ${dataIT(a.periodo_a)}`}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                {ETICHETTA_FILE[tipo]}
              </button>
            ))}
        </div>
      ),
    },
    {
      key: "copia",
      header: "Copia su SharePoint",
      render: (a) => (
        <div className="flex max-w-xs flex-col gap-1 text-xs">
          {a.copia_esterna_at && (
            <span className="text-ink/70">Copiato il {new Date(a.copia_esterna_at).toLocaleString("it-IT")}</span>
          )}
          {a.copia_esterna_errore && (
            <span className="text-danger">
              {a.copia_esterna_at ? "Ultimo tentativo non riuscito: " : "Copia non riuscita: "}
              {a.copia_esterna_errore}
            </span>
          )}
          {a.copia_esterna_errore && puoArchiviare && (
            <span>
              <Button size="sm" variant="secondary" loading={inCopia === a.id} onClick={() => onRicopia(a)}>
                <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                Ricopia
              </Button>
            </span>
          )}
        </div>
      ),
    },
    {
      key: "caricato",
      header: "Archiviato da",
      render: (a) => (
        <span className="text-ink/70">
          {a.creatore?.full_name ?? a.creatore?.email ?? "—"}
          <span className="block text-xs text-ink/50">{new Date(a.created_at).toLocaleDateString("it-IT")}</span>
        </span>
      ),
    },
  ];
  if (puoTogliere) {
    colonne.push({
      key: "azioni",
      header: "",
      render: (a) => (
        <button
          type="button"
          onClick={() => onTogli(a)}
          className="rounded-lg p-1.5 text-ink/40 shadow-none transition-[background-color,color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-danger/10 hover:text-danger hover:shadow-card active:translate-y-0 active:shadow-none"
          aria-label={`Togli l'archivio del periodo dal ${dataIT(a.periodo_da)} al ${dataIT(a.periodo_a)}`}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      ),
    });
  }

  return (
    <section className="mt-8 rounded-card border border-line bg-white p-5 shadow-card">
      <div className="mb-4 flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink/50 shadow-card">
          <Archive className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-lg font-bold text-ink">Periodi vidimati</h2>
          <p className="text-sm text-ink/50">
            Gli estratti firmati e marcati con Aruba, e gli anni del registro che ancora non ne hanno uno.
          </p>
        </div>
      </div>

      {dati && !dati.sharepoint_configurato && (
        <p className="mb-4 rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-ink/80">
          La copia su SharePoint non è configurata su questo server: gli archivi si salvano, ma restano in
          un'unica copia.
        </p>
      )}

      {dati && dati.anni.length > 0 && (
        <ul className="mb-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {dati.anni.map((c) => (
            <li key={c.anno} className="rounded-lg border border-line bg-surface p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold tabular-nums text-ink">{c.anno}</span>
                <Badge color={COLORE_VIDIMAZIONE[c.stato]}>{registroApi.STATO_VIDIMAZIONE_LABEL[c.stato]}</Badge>
              </div>
              <p className="mt-1 text-xs text-ink/60">
                {c.registrazioni} {c.registrazioni === 1 ? "registrazione" : "registrazioni"}
              </p>
              {c.stato === "parziale" && (
                <p className="mt-1 text-xs text-ink/60">
                  Scoperto {c.scoperture.map((x) => `dal ${dataIT(x.da)} al ${dataIT(x.a)}`).join("; ")}.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <Table
        columns={colonne}
        data={dati?.archivi ?? []}
        rowKey={(a) => a.id}
        isLoading={dati === null}
        emptyMessage="Nessun periodo archiviato finora."
      />
    </section>
  );
}

/** Quanti caratteri deve avere il motivo: la stessa soglia del backend. */
const MOTIVO_MINIMO = 10;

/**
 * Togliere un archivio: solo admin, e con un motivo che finisce nel registro
 * delle attività insieme all'archivio (decisione di Fabio del 10/09/2026).
 */
function TogliArchivioModal({
  archivio,
  onClose,
  onTolto,
}: {
  archivio: registroApi.ArchivioRegistro | null;
  onClose: () => void;
  onTolto: () => void;
}) {
  const { showToast } = useToast();
  const [motivo, setMotivo] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const idMotivo = useId();

  useEffect(() => {
    setMotivo("");
  }, [archivio?.id]);

  const lunghezza = motivo.trim().length;

  async function togli() {
    if (!archivio || lunghezza < MOTIVO_MINIMO) return;
    setIsDeleting(true);
    try {
      const r = await registroApi.eliminaArchivio(archivio.id, motivo.trim());
      showToast(
        r.copia_esterna_conservata
          ? "Archivio tolto dall'elenco. I file restano sul server, e la copia su SharePoint resta dov'è."
          : "Archivio tolto dall'elenco. I file restano sul server, fra gli archivi cancellati.",
        "success"
      );
      onTolto();
      onClose();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile togliere l'archivio."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Modal
      open={Boolean(archivio)}
      onClose={onClose}
      title="Togliere un archivio"
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={isDeleting}
            disabled={lunghezza < MOTIVO_MINIMO}
            onClick={togli}
          >
            Togli l'archivio
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink/70">
          Stai per togliere l'archivio del periodo{" "}
          <strong>
            dal {archivio ? dataIT(archivio.periodo_da) : ""} al {archivio ? dataIT(archivio.periodo_a) : ""}
          </strong>
          . È la prova che quel periodo del registro è stato firmato e marcato: si toglie solo per rifarlo, se
          il file marcato era sbagliato.
        </p>
        <p className="text-sm text-ink/70">
          I file non vengono distrutti: sul server finiscono fra gli archivi cancellati, e la copia su
          SharePoint resta dov'è.
        </p>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={idMotivo} className="text-sm font-medium text-ink">
            Motivo<span className="text-accent"> *</span>
          </label>
          <textarea
            id={idMotivo}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink placeholder:text-ink/40 focus-visible:border-primary focus-visible:shadow-[0_0_0_3px_rgba(24,39,61,0.12)]"
            placeholder="Per esempio: marcato il PDF del periodo sbagliato, da rifare."
          />
          <p className="text-xs text-ink/50">
            {lunghezza < MOTIVO_MINIMO
              ? `Almeno ${MOTIVO_MINIMO} caratteri (${lunghezza} finora).`
              : "Resta scritto nel registro delle attività, insieme all'archivio tolto e al tuo nome."}
          </p>
        </div>
      </div>
    </Modal>
  );
}

const COLORE_ESITO: Record<EsitoAffare, BadgeColor> = {
  in_corso_di_esecuzione: "primary",
  rinnovo_anno_precedente: "accent",
  concluso: "success",
  // Non `danger`: un affare interrotto non è un errore, è un esito.
  interrotto_anticipatamente: "warning",
};

/**
 * Una data del registro, come si scrive in italiano.
 *
 * Formattata dai PEZZI DELLA STRINGA e non passando da `new Date`: il backend
 * manda una data-sola (colonna `DATE`) serializzata a mezzanotte UTC, e
 * rileggerla con un oggetto `Date` la reinterpreta nel fuso del browser. Da noi
 * il giorno non arretra mai — l'Italia è sempre avanti rispetto a UTC — ma su
 * un registro di legge la data non deve dipendere da dove si trova chi guarda.
 */
function dataIT(iso: string | null): string {
  if (!iso) return "";
  const [anno, mese, giorno] = iso.slice(0, 10).split("-");
  return giorno && mese && anno ? `${giorno}/${mese}/${anno}` : iso;
}

/** Un importo come lo scrive il registro: `225.5` → `225,50 €`. */
function importoIT(v: string | null): string | null {
  if (v === null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return `${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/**
 * Un importo scritto nel form, letto come numero.
 *
 * Rispecchia `importo()` del backend: la virgola decimale va bene e il punto
 * delle migliaia si toglie. Serve per CONFRONTARE, non per salvare — il valore
 * spedito resta la stringa che l'utente ha scritto, e a interpretarla e'
 * comunque il backend.
 */
function comeNumero(v: string): number {
  const s = v.includes(",") ? v.replace(/\./g, "").replace(",", ".") : v;
  return Number(s);
}

/**
 * Due importi sono lo stesso importo?
 *
 * Confronto NUMERICO e non testuale: `50`, `50.00` e `50,00` sono lo stesso
 * compenso, e riscriverne uno nell'altro non e' una modifica. Il confronto
 * testuale accenderebbe il Salva e farebbe scrivere nell'audit log una
 * «registrazione modificata» a fronte di niente — la stessa traccia falsa che
 * il pulsante spento serve a evitare.
 *
 * Vuoto contro valorizzato resta un cambiamento vero: togliere un compenso e'
 * un'affermazione, non un ritocco di forma.
 */
function stessoImporto(scritto: string, originale: string | null): boolean {
  const a = scritto.trim();
  const b = (originale ?? "").trim();
  if (a === b) return true;
  if (!a || !b) return false;
  const na = comeNumero(a);
  const nb = comeNumero(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

/**
 * Il vuoto dichiarato.
 *
 * Un trattino direbbe «campo vuoto», che su un registro non basta: `null` su
 * una data o su un compenso è un'affermazione — quel dato non risulta — e va
 * letta come tale da chi consulta il registro anni dopo.
 */
function NonRisulta() {
  return <span className="text-ink/35 italic">non risulta</span>;
}

// ============================================================================
// Modal — nuova registrazione a mano
// ============================================================================
function NuovaRigaModal({
  open,
  onClose,
  onCreata,
  naturaPredefinita,
  annoCorrente,
}: {
  open: boolean;
  onClose: () => void;
  onCreata: () => void;
  naturaPredefinita: string;
  annoCorrente: number;
}) {
  const { showToast } = useToast();
  const [data, setData] = useState("");
  const [anno, setAnno] = useState(String(annoCorrente));
  const [generalitaA, setGeneralitaA] = useState("");
  const [generalitaB, setGeneralitaB] = useState("");
  const [natura, setNatura] = useState(naturaPredefinita);
  const [pattuito, setPattuito] = useState("");
  const [percepito, setPercepito] = useState("");
  const [esito, setEsito] = useState<EsitoAffare>("in_corso_di_esecuzione");
  const [note, setNote] = useState("");
  const [contrattoId, setContrattoId] = useState("");
  const [contratti, setContratti] = useState<Contratto[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData("");
    setAnno(String(annoCorrente));
    setGeneralitaA("");
    setGeneralitaB("");
    setNatura(naturaPredefinita);
    setPattuito("");
    setPercepito("");
    setEsito("in_corso_di_esecuzione");
    setNote("");
    setContrattoId("");
    // I contratti servono solo per collegare una riga storica al suo
    // contratto: si caricano all'apertura e non prima, perché nella maggior
    // parte dei casi questa modale non si apre mai.
    contrattiApi
      .listContratti()
      .then((r) => setContratti(r.contratti))
      .catch(() => setContratti([]));
  }, [open, naturaPredefinita, annoCorrente]);

  // La data comanda sull'anno: se c'è, l'anno è il suo e non si tocca. È la
  // stessa regola del backend, mostrata invece che lasciata scoprire con un
  // errore dopo il salvataggio.
  //
  // L'anno si legge dai primi quattro caratteri e non da `new Date(data)`:
  // `<input type="date">` produce già `AAAA-MM-GG`, e farne un oggetto `Date`
  // lo interpreta come mezzanotte UTC per poi rileggerlo nel fuso del browser.
  // Qui quel valore decide il NUMERO D'ORDINE di un registro di legge: non può
  // dipendere da dove si trova chi compila.
  const annoDallaData = data ? data.slice(0, 4) : null;
  const annoEffettivo = annoDallaData ?? anno;

  async function salva() {
    setIsSaving(true);
    try {
      await registroApi.creaRiga({
        data: data || null,
        anno: data ? undefined : Number(anno),
        generalita_a: generalitaA,
        generalita_b: generalitaB || null,
        natura_operazione: natura,
        compenso_pattuito: pattuito || null,
        compenso_percepito: percepito || null,
        esito_affare: esito,
        note: note || null,
        contratto_id: contrattoId || null,
      });
      showToast("Registrazione scritta nel registro.", "success");
      onClose();
      onCreata();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile scrivere la registrazione."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  // Gli STESSI bound di `annoDellaRiga()` lato backend: intero fra 1900 e 2999.
  // Ripetuti qui non per sicurezza — quella la fa il backend — ma perché il
  // pulsante non deve restare attivo su un valore che verrà rifiutato: è il
  // principio dichiarato in testa a questa pagina, il vincolo si vede prima.
  const n = Number(annoEffettivo);
  const annoValido = Number.isInteger(n) && n >= 1900 && n <= 2999;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nuova registrazione"
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            type="button"
            loading={isSaving}
            disabled={!generalitaA.trim() || !natura.trim() || !annoValido}
            onClick={salva}
          >
            Scrivi nel registro
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="rounded-lg border border-line bg-surface p-3 text-sm text-ink/60">
          Le registrazioni dei contratti firmati nascono da sole alla firma. Questa serve
          all'affare che da un contratto di Cash Management non viene, e allo storico.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Data dell'operazione"
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            hint="Può restare vuota: sul registro storico spesso non risulta."
          />
          <Input
            label="Anno del numero d'ordine"
            type="number"
            value={annoEffettivo}
            disabled={Boolean(data)}
            onChange={(e) => setAnno(e.target.value)}
            required
            hint={
              data
                ? "Lo detta la data."
                : "Senza data va indicato: sul registro si legge dal numero stesso (1-24 → 2024)."
            }
          />
        </div>

        <Input
          label="A) Generalità di chi effettua l'operazione"
          value={generalitaA}
          onChange={(e) => setGeneralitaA(e.target.value)}
          required
          hint="Nome, domicilio e partita IVA, come risultano oggi: restano scritti qui, non si rileggono dall'anagrafica."
        />
        <Input
          label="B) Generalità del soggetto per conto del quale si opera"
          value={generalitaB}
          onChange={(e) => setGeneralitaB(e.target.value)}
          hint="Quasi sempre vuoto: EVO tratta col committente diretto."
        />
        <Input
          label="Natura dell'operazione"
          value={natura}
          onChange={(e) => setNatura(e.target.value)}
          required
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Compenso pattuito"
            value={pattuito}
            onChange={(e) => setPattuito(e.target.value)}
            hint="Vuoto se non risulta un importo. Una formula («50€/mese + 0,27%») va nelle note."
          />
          <Input
            label="Compenso percepito"
            value={percepito}
            onChange={(e) => setPercepito(e.target.value)}
            hint="Si sa dopo: normale lasciarlo vuoto adesso."
          />
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink">Esito dell'affare</span>
          <select
            value={esito}
            onChange={(e) => setEsito(e.target.value as EsitoAffare)}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink shadow-card transition-[border-color,box-shadow] duration-150 ease-out hover:border-primary/30 hover:shadow-lift focus:border-primary/40 focus:outline-none"
          >
            {(Object.keys(registroApi.ESITO_LABEL) as EsitoAffare[]).map((e) => (
              <option key={e} value={e}>
                {registroApi.ESITO_LABEL[e]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink">Contratto di provenienza</span>
          <select
            value={contrattoId}
            onChange={(e) => setContrattoId(e.target.value)}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink shadow-card transition-[border-color,box-shadow] duration-150 ease-out hover:border-primary/30 hover:shadow-lift focus:border-primary/40 focus:outline-none"
          >
            <option value="">Nessuno — affare non legato a un contratto</option>
            {contratti.map((c) => (
              <option key={c.id} value={c.id}>
                {c.cliente?.nome ?? "senza cliente"}
                {c.riferimento_contratto ? ` — ${c.riferimento_contratto}` : ""}
                {` (${contrattiApi.STATO_LABEL[c.stato]})`}
              </option>
            ))}
          </select>
          <span className="text-xs text-ink/50">
            Per lo storico: tiene la riga attaccata al suo contratto. Un rinnovo si registra
            nell'anno del rinnovo, non due volte nello stesso.
          </span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink">Note</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            placeholder="Es. 50€/mese + 0,27% sul transato in cassaforte"
          />
        </label>
      </div>
    </Modal>
  );
}

// ============================================================================
// Modal — completa una registrazione
//
// Il caso normale non è correggere: è COMPLETARE. La riga nasce alla firma con
// il percepito ignoto e l'affare in corso, e questi due si sanno dopo.
// ============================================================================
function CompletaRigaModal({
  riga,
  onClose,
  onSalvata,
}: {
  riga: RigaRegistroAffari | null;
  onClose: () => void;
  onSalvata: () => void;
}) {
  const { showToast } = useToast();
  const [data, setData] = useState("");
  const [generalitaA, setGeneralitaA] = useState("");
  const [generalitaB, setGeneralitaB] = useState("");
  const [pattuito, setPattuito] = useState("");
  const [percepito, setPercepito] = useState("");
  const [esito, setEsito] = useState<EsitoAffare>("in_corso_di_esecuzione");
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!riga) return;
    setData(riga.data ? riga.data.slice(0, 10) : "");
    setGeneralitaA(riga.generalita_a);
    setGeneralitaB(riga.generalita_b ?? "");
    setPattuito(riga.compenso_pattuito ?? "");
    setPercepito(riga.compenso_percepito ?? "");
    setEsito(riga.esito_affare);
    setNote(riga.note ?? "");
  }, [riga]);

  async function salva() {
    if (!riga) return;
    setIsSaving(true);
    try {
      await registroApi.aggiornaRiga(riga.id, {
        data: data || null,
        generalita_a: generalitaA,
        generalita_b: generalitaB || null,
        compenso_pattuito: pattuito || null,
        compenso_percepito: percepito || null,
        esito_affare: esito,
        note: note || null,
      });
      showToast(`Registrazione ${riga.numero_ordine} aggiornata.`, "success");
      onClose();
      onSalvata();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiornare la registrazione."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  // IL SALVA RESTA SPENTO FINCHE' NON CAMBIA DAVVERO QUALCOSA.
  //
  // Non e' pulizia estetica: salvare senza modifiche scriverebbe nell'audit log
  // una riga «registrazione modificata», con l'elenco dei campi, a fronte di
  // niente. Su un registro di legge una traccia che dice il falso e' peggio di
  // una traccia assente — stessa ragione per cui un campo vuoto non si scrive
  // con un trattino qualunque.
  const cambiato = riga
    ? data !== (riga.data ? riga.data.slice(0, 10) : "") ||
      generalitaA !== riga.generalita_a ||
      generalitaB !== (riga.generalita_b ?? "") ||
      !stessoImporto(pattuito, riga.compenso_pattuito) ||
      !stessoImporto(percepito, riga.compenso_percepito) ||
      esito !== riga.esito_affare ||
      note !== (riga.note ?? "")
    : false;

  return (
    <Modal
      open={Boolean(riga)}
      onClose={onClose}
      title={riga ? `Registrazione ${riga.numero_ordine}` : ""}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            type="button"
            loading={isSaving}
            disabled={!generalitaA.trim() || !cambiato}
            onClick={salva}
          >
            Salva
          </Button>
        </>
      }
    >
      {riga && (
        <div className="flex flex-col gap-4">
          <p className="rounded-lg border border-line bg-surface p-3 text-sm text-ink/60">
            Il numero d'ordine <strong>{riga.numero_ordine}</strong> non si modifica, e la data
            deve restare nel {riga.anno}: per registrare l'affare in un altro anno la riga va
            cancellata e riscritta.
          </p>

          <Input
            label="Data dell'operazione"
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            hint={`Dev'essere nel ${riga.anno}. Si può svuotare: significa «non risulta».`}
          />
          <Input
            label="A) Generalità di chi effettua l'operazione"
            value={generalitaA}
            onChange={(e) => setGeneralitaA(e.target.value)}
            required
          />
          <Input
            label="B) Generalità del soggetto per conto del quale si opera"
            value={generalitaB}
            onChange={(e) => setGeneralitaB(e.target.value)}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Compenso pattuito"
              value={pattuito}
              onChange={(e) => setPattuito(e.target.value)}
            />
            <Input
              label="Compenso percepito"
              value={percepito}
              onChange={(e) => setPercepito(e.target.value)}
              hint="Quanto è stato davvero incassato."
            />
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink">Esito dell'affare</span>
            <select
              value={esito}
              onChange={(e) => setEsito(e.target.value as EsitoAffare)}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink shadow-card transition-[border-color,box-shadow] duration-150 ease-out hover:border-primary/30 hover:shadow-lift focus:border-primary/40 focus:outline-none"
            >
              {(Object.keys(registroApi.ESITO_LABEL) as EsitoAffare[]).map((e) => (
                <option key={e} value={e}>
                  {registroApi.ESITO_LABEL[e]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink">Note</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            />
          </label>

          {riga.contratto && (
            <p className="text-sm text-ink/50">
              Nasce dal contratto di <strong>{riga.contratto.cliente?.nome ?? "cliente ignoto"}</strong>
              {riga.contratto.riferimento_contratto ? ` (${riga.contratto.riferimento_contratto})` : ""}.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Costante e non `[]` in linea: un array nuovo a ogni render farebbe ripartire l'effetto del pannello. */
const NESSUNA_RIGA: RigaRegistroAffari[] = [];

// ============================================================================
// Pagina
// ============================================================================
export default function RegistroAffari() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [righe, setRighe] = useState<RigaRegistroAffari[]>([]);
  const [anni, setAnni] = useState<number[]>([]);
  const [natura, setNatura] = useState("");
  // Anno (`?anno=`), ricerca (`?ricerca=`), ordinamento e riga aperta
  // (`?open=`) nell'URL: tornando indietro o aprendo un link si ritrova la
  // stessa vista. La ricerca filtra subito sul testo digitato, come prima;
  // nell'URL arriva col solito ritardo, per non riscriverlo a ogni tasto.
  const [searchParams] = useSearchParams();
  const scrivi = useScriviParametri();
  const { ordine, onOrdineChange } = useOrdinamentoUrl();
  const annoUrl = Number(searchParams.get("anno"));
  const anno: number | "" = Number.isInteger(annoUrl) && annoUrl > 0 ? annoUrl : "";
  const setAnno = (a: number | "") => scrivi({ anno: a === "" ? null : String(a) });
  const { testo: ricerca, setTesto: setRicerca } = useRicercaUrl();
  const [estrattoOpen, setEstrattoOpen] = useState(false);
  const [archiviaOpen, setArchiviaOpen] = useState(false);
  const [vidimazioni, setVidimazioni] = useState<Awaited<ReturnType<typeof registroApi.listArchivi>> | null>(null);
  const [archivioDaTogliere, setArchivioDaTogliere] = useState<registroApi.ArchivioRegistro | null>(null);
  const [inCopia, setInCopia] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nuovaOpen, setNuovaOpen] = useState(false);
  const [rigaAperta, setRigaAperta] = useState<RigaRegistroAffari | null>(null);
  const [daCancellare, setDaCancellare] = useState<RigaRegistroAffari | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const carica = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await registroApi.listRegistro(anno === "" ? undefined : anno);
      setRighe(r.righe);
      setAnni(r.anni);
      setNatura(r.natura_predefinita);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile leggere il registro."), "error");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anno]);

  useEffect(() => {
    carica();
  }, [carica]);

  const caricaVidimazioni = useCallback(async () => {
    try {
      setVidimazioni(await registroApi.listArchivi());
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile leggere gli archivi del registro."), "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    caricaVidimazioni();
  }, [caricaVidimazioni]);

  async function ricopia(a: registroApi.ArchivioRegistro) {
    setInCopia(a.id);
    try {
      const r = await registroApi.ricopiaArchivio(a.id);
      if (r.copia_esterna_errore) {
        showToast(`La copia su SharePoint non è riuscita neanche stavolta: ${r.copia_esterna_errore}`, "warning");
      } else {
        showToast("Copiato su SharePoint.", "success");
      }
      caricaVidimazioni();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile ritentare la copia."), "error");
    } finally {
      setInCopia(null);
    }
  }

  /**
   * L'ordine del registro: numero d'ordine CRESCENTE dentro l'anno, anni più
   * recenti sopra. Il backend le manda decrescenti perché aprendo la pagina si
   * vuole l'ultima scritta, ma leggendo un anno si legge dall'inizio, come su
   * un giornale.
   */
  /**
   * La ricerca guarda `generalita_a`, che è dove sta il committente: ragione
   * sociale, domicilio e partita IVA in un testo solo, congelato.
   *
   * Due confronti e non uno. Il primo è la stringa così com'è, per il nome. Il
   * secondo toglie tutto ciò che non è lettera o cifra da entrambe le parti,
   * perché una partita IVA sul registro si scrive in modi diversi — `P.IVA
   * IT07009860482` sulle righe nuove, `p.iva 08635260014` su quelle storiche
   * congelate dal foglio Excel — e chi cerca digita solo le cifre. Senza il
   * secondo confronto, cercare `07009860482` non troverebbe la riga che quella
   * partita IVA ce l'ha scritta sopra.
   *
   * Filtra soltanto ciò che è già stato caricato: l'anno lo sceglie il menu qui
   * accanto e quello sì che ricarica. È una lente sulla pagina aperta, non una
   * seconda interrogazione.
   */
  const righeOrdinate = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    const qCifre = q.replace(/[^a-z0-9]/g, "");

    const filtrate = q
      ? righe.filter((r) => {
          const testo = `${r.generalita_a}\n${r.generalita_b ?? ""}`.toLowerCase();
          if (testo.includes(q)) return true;
          return qCifre.length > 0 && testo.replace(/[^a-z0-9]/g, "").includes(qCifre);
        })
      : righe;

    return [...filtrate].sort((a, b) => b.anno - a.anno || a.progressivo - b.progressivo);
  }, [righe, ricerca]);

  // La modale di modifica su `?open=<id>`. Si apre solo a chi può modificare:
  // per gli altri il clic sulla riga non c'è, e un `?open=` arrivato da un link
  // si toglie senza aprire niente — come una riga che non è fra quelle
  // caricate (altro anno, riga cancellata).
  const puoModificare = hasPermission("registro_affari:update");
  const pannello = usePannelloRecord({
    records: puoModificare ? righe : NESSUNA_RIGA,
    isLoading,
    preparaNuovo: () => undefined,
    preparaModifica: setRigaAperta,
  });
  const inModifica = pannello.apertoModifica ? rigaAperta : null;

  async function cancella() {
    if (!daCancellare) return;
    setIsDeleting(true);
    try {
      await registroApi.eliminaRiga(daCancellare.id);
      showToast(`Registrazione ${daCancellare.numero_ordine} cancellata.`, "success");
      setDaCancellare(null);
      carica();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile cancellare la registrazione."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  const colonne: TableColumn<RigaRegistroAffari>[] = [
    {
      key: "numero",
      header: "N. d'ordine",
      sempreVisibile: true,
      sortValue: (r) => r.anno * 100000 + r.progressivo,
      // Il numero d'ordine e' cio' che rende quella riga una registrazione e non
      // un record: tabellare, cosi' le cifre si incolonnano leggendo, e dentro
      // una targhetta che lo stacca dal resto della riga.
      render: (r) => (
        <span className="inline-flex items-center rounded-lg border border-line bg-surface px-2 py-0.5 font-semibold tabular-nums text-ink shadow-card">
          {r.numero_ordine}
        </span>
      ),
    },
    {
      key: "data",
      header: "Data",
      sortValue: (r) => r.data ?? "",
      render: (r) => (r.data ? dataIT(r.data) : <NonRisulta />),
    },
    {
      key: "generalita",
      header: "Generalità di chi effettua l'operazione",
      sortValue: (r) => r.generalita_a,
      render: (r) => (
        // Le generalità sono composte su più righe (nome, domicilio, P.IVA) e
        // vanno lette così: `whitespace-pre-line` tiene gli a capo che il
        // backend ha scritto.
        <span className="whitespace-pre-line text-ink/80">{r.generalita_a}</span>
      ),
    },
    {
      key: "natura",
      header: "Natura dell'operazione",
      sortValue: (r) => r.natura_operazione,
      render: (r) => r.natura_operazione,
    },
    {
      key: "pattuito",
      header: "Compenso pattuito",
      sortValue: (r) => (r.compenso_pattuito === null ? null : Number(r.compenso_pattuito)),
      render: (r) => importoIT(r.compenso_pattuito) ?? <NonRisulta />,
    },
    {
      key: "percepito",
      header: "Compenso percepito",
      sortValue: (r) => (r.compenso_percepito === null ? null : Number(r.compenso_percepito)),
      render: (r) => importoIT(r.compenso_percepito) ?? <NonRisulta />,
    },
    {
      key: "esito",
      header: "Esito",
      sortValue: (r) => r.esito_affare,
      render: (r) => (
        <Badge color={COLORE_ESITO[r.esito_affare]}>{registroApi.ESITO_LABEL[r.esito_affare]}</Badge>
      ),
    },
    {
      key: "note",
      header: "Note",
      sortValue: (r) => r.note ?? "",
      render: (r) => (r.note ? <span className="text-ink/70">{r.note}</span> : "—"),
    },
  ];

  if (hasPermission("registro_affari:delete")) {
    colonne.push({
      key: "azioni",
      header: "",
      render: (r) => (
        // Stessa fisicita' dei Button — sollevamento su hover, appoggio sul
        // click — riscritta qui a mano perche' e' un `button` nudo dentro la
        // tabella e non il componente condiviso.
        //
        // ROSSO, non l'oro del resto dell'interfaccia. A riposo l'icona e'
        // grigia e il colore compare solo al passaggio del mouse: e' l'istante
        // prima di premere, ed e' esattamente li' che serve dire che questa
        // cancella una registrazione di un registro di legge. Dipende dal
        // token `danger` aggiunto nella PR #139 — prima di quel merge la
        // classe non emette CSS e l'icona resta grigia.
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setDaCancellare(r);
          }}
          className="rounded-lg p-1.5 text-ink/40 shadow-none transition-[background-color,color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-danger/10 hover:text-danger hover:shadow-card active:translate-y-0 active:shadow-none"
          aria-label={`Cancella la registrazione ${r.numero_ordine}`}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      ),
    });
  }

  return (
    <div>
      {/* La testata e' una tessera e non un titolo appoggiato sul fondo: il
          registro e' un documento, e dargli un bordo lo separa dalla pagina
          come un fascicolo dal tavolo. Stessi token delle sezioni di
          `Contratti.tsx` — `rounded-card`, `border-line`, `shadow-card` — non
          uno stile nuovo. */}
      <header className="mb-6 flex flex-col gap-4 rounded-card border border-line bg-white p-5 shadow-card sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          {/* Il tondo col lucchetto: profondita' senza colori nuovi, il navy
              del testo a bassa opacita' su una superficie gia' esistente. */}
          <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink/50 shadow-card">
            <BookLock className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink">Registro Giornale degli Affari</h1>
            <p className="text-sm text-ink/50">
              Art. 120 TULPS — le registrazioni dei contratti firmati nascono da sole.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Prima dell'anno: si cerca un committente più spesso di quanto si
              cambi fascicolo. */}
          <div className="w-full sm:w-64">
            <Input
              type="search"
              placeholder="Cerca committente o P. IVA…"
              aria-label="Cerca per ragione sociale o partita IVA"
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
            />
          </div>
          <select
            value={anno}
            onChange={(e) => setAnno(e.target.value === "" ? "" : Number(e.target.value))}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink shadow-card transition-[border-color,box-shadow] duration-150 ease-out hover:border-primary/30 hover:shadow-lift focus:border-primary/40 focus:outline-none"
            aria-label="Anno del registro"
          >
            <option value="">Tutti gli anni</option>
            {anni.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          {/* Secondario: si consulta il registro tutti i giorni, lo si estrae
              per la firma due volte l'anno. */}
          <Button variant="secondary" onClick={() => setEstrattoOpen(true)}>
            <FileSignature className="h-4 w-4" aria-hidden="true" />
            Estratto per la firma
          </Button>
          {/* Accanto all'estratto: sono i due tempi dello stesso gesto — si
              scarica, si porta da Aruba, si ricarica. */}
          {hasPermission("registro_affari:archivia") && (
            <Button variant="secondary" onClick={() => setArchiviaOpen(true)}>
              <Archive className="h-4 w-4" aria-hidden="true" />
              Archivia periodo firmato
            </Button>
          )}
          {hasPermission("registro_affari:create") && (
            <Button onClick={() => setNuovaOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuova registrazione
            </Button>
          )}
        </div>
      </header>

      {/* `ordinabile` e' una comodita' di CONSULTAZIONE, non una deroga
          all'ordine del registro: la vista di partenza e' sempre quella
          canonica — numero d'ordine crescente dentro l'anno — e ricaricando la
          pagina ci si torna. Cliccare un'intestazione non riscrive niente,
          serve a rispondere a domande legittime («tutte le righe di questo
          cliente», «i compensi piu' alti») senza dover leggere tutto. */}
      <Table
        columns={colonne}
        ordinabile
        ordine={ordine}
        onOrdineChange={onOrdineChange}
        sceltaColonne="registro-affari"
        data={righeOrdinate}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        emptyMessage={
          // Con una ricerca attiva il registro non è vuoto: è la ricerca che non
          // trova. Dirlo evita di far credere che manchino delle registrazioni.
          ricerca.trim()
            ? `Nessun committente corrisponde a «${ricerca.trim()}»${
                anno === "" ? "" : ` nel ${anno}`
              }.`
            : anno === ""
              ? "Il registro è vuoto: nessun affare ancora registrato."
              : `Nessuna registrazione nel ${anno}.`
        }
        onRowClick={puoModificare ? (r) => pannello.apri(r.id) : undefined}
      />

      <SezioneVidimazioni
        dati={vidimazioni}
        puoArchiviare={hasPermission("registro_affari:archivia")}
        puoTogliere={hasPermission("registro_affari:delete")}
        inCopia={inCopia}
        onRicopia={ricopia}
        onTogli={setArchivioDaTogliere}
      />

      <ArchiviaModal
        open={archiviaOpen}
        onClose={() => setArchiviaOpen(false)}
        anni={anni}
        annoCorrente={new Date().getFullYear()}
        archivi={vidimazioni?.archivi ?? []}
        onArchiviato={caricaVidimazioni}
      />

      <TogliArchivioModal
        archivio={archivioDaTogliere}
        onClose={() => setArchivioDaTogliere(null)}
        onTolto={caricaVidimazioni}
      />

      <EstrattoModal
        open={estrattoOpen}
        onClose={() => setEstrattoOpen(false)}
        anni={anni}
        annoCorrente={new Date().getFullYear()}
      />

      <NuovaRigaModal
        open={nuovaOpen}
        onClose={() => setNuovaOpen(false)}
        onCreata={() => {
          carica();
          caricaVidimazioni();
        }}
        naturaPredefinita={natura}
        annoCorrente={new Date().getFullYear()}
      />

      <CompletaRigaModal
        riga={inModifica}
        onClose={pannello.chiudi}
        onSalvata={carica}
      />

      <Modal
        open={Boolean(daCancellare)}
        onClose={() => setDaCancellare(null)}
        title="Cancellare una registrazione"
        size="sm"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setDaCancellare(null)}>
              Annulla
            </Button>
            <Button type="button" variant="danger" loading={isDeleting} onClick={cancella}>
              Cancella
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink/70">
          Stai per togliere la registrazione <strong>{daCancellare?.numero_ordine}</strong> dal
          Registro Giornale degli Affari. Non è un record qualunque: è una scrittura che la legge
          impone di tenere, e il suo numero d'ordine non verrà riusato.
        </p>
        <p className="mt-3 text-sm text-ink/70">
          Per l'affare che non è andato in porto esiste l'esito, che lascia la traccia invece di
          toglierla. La cancellazione resta scritta nel registro delle attività, col tuo nome.
        </p>
      </Modal>
    </div>
  );
}
